import CustomMetaData, {
  CustomMetaDataProps,
} from 'components/general/customMetaData/CustomMetaData';
import { StandardBlocksLayout } from 'components/general/standardBlocksLayout/StandardBlocksLayout';
import { BlockType } from 'components/general/switchBlock/SwitchBlock';
import { ProjectCategoryType } from 'components/projectCategories/ProjectCategories';
import { ProjectSelectorCarousel } from 'components/projectSelectorCarousel/ProjectSelectorCarousel';
import { useThemeContext } from 'context/ThemeContext';
import { gsap } from 'gsap/dist/gsap';
import { NEXTJS_REVALIDATE_PAGE_IN_SECONDS } from 'pages/index';
import { useEffect, useRef, useState } from 'react';
import { getEndpoint, getData, getCSSVar, isMobileWidth } from 'utils/helpers';
import { getGlobalData, GlobalDataType } from 'utils/parsers';
import BackToProjects from 'components/project/BackToProjects';
import style from './projectPage.module.scss';
import SVG from './uparrow.svg';

import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin';
import { useRouter } from 'next/router';
import ProjectContent from 'components/project/ProjectContent';

if (typeof window !== 'undefined') {
  // Due to SSR we can only register when we are in the browser
  gsap.registerPlugin(ScrollToPlugin);
}

export type ProjectType = {
  url: string;
  slug: string;
  full_name: string;
  tagline: string;
  blurb: string;
  hero: MediaType;
  thumb: MediaType;
  categories: ProjectCategoryType[];
  blocks: BlockType[];
  meta_title: string;
  meta_description: string;
  meta_image: string;
  deliverables: string;
  product: string;
  theme_color?: string | undefined;
  is_featured: boolean;
};

export type ImageType = {
  image: string;
  image_width: number;
  image_height: number;
  alt_text: string;
};

export type MediaType = {
  image: string;
  image_width: number;
  image_height: number;
  alt_text: string;

  phone_image_height: number | null;
  phone_image_width: number | null;
  phone_image: string | null;

  video: string | null;
  phone_video: string | null;
  autoplay: boolean;
  controls: boolean;
  loop: boolean;
  full_width: boolean;
};

type Props = { globalData: GlobalDataType; project: ProjectType; projects: ProjectType[] } & {
  metaData: CustomMetaDataProps;
};

const Project = ({ project, projects, metaData, globalData }: Props) => {
  const { setTheme } = useThemeContext();
  const router = useRouter();

  const [projectToBeFetched, setProjectToBeFetched] = useState<ProjectType | undefined>(undefined);
  const darkener = useRef<HTMLDivElement>(null);
  const contentButton = useRef<HTMLDivElement>(null);
  const deliverablesTextRef = useRef<HTMLDivElement>(null);

  const onCaseStudyClick = () => {
    const gutterRem = getCSSVar('--page-gutter');
    const gutterPixels = gutterRem * 16;
    gsap.to(window, { duration: 0.5, scrollTo: { y: '#projectContent', offsetY: gutterPixels } });
  };

  const onNextClick = () => {
    const checkedAgains = projectToBeFetched ? projectToBeFetched.slug : project.slug;
    const index = projects.findIndex((p) => p.slug === checkedAgains);
    const nextIndex = index + 1 < projects.length ? index + 1 : 0;
    const nextProject = projects[nextIndex];
    onCarouselSlideSelect(nextProject);
  };

  const onCarouselSlideSelect = (project: ProjectType) => {
    // we scroll up because the content changes and we don't want the user to get confused
    gsap.to(window, { duration: 0.5, scrollTo: 0 });
    // shallow naviation, doesn't load the new route
    router.push(`/project/${project.slug}`, undefined, { shallow: true });
  };

  const onScrollToTopClick = () => {
    gsap.to(window, { scrollTo: 0 });
  };
  const onScroll = (e: Event) => {
    // kill sneak peek animation
    hidePeek();
    const onMobile = isMobileWidth(window.innerWidth);
    const max = onMobile ? window.innerHeight : window.innerHeight / 2;
    const currentScroll = window.scrollY;
    const pct = Math.min(1, currentScroll / max);
    const maxOpacity = onMobile ? 1 : 0.7;
    gsap.to(darkener.current, { alpha: pct * pct * maxOpacity });
    gsap.to(contentButton.current, { alpha: 1 - pct * pct });
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    setTheme('dark');
    if (window.scrollY === 0 && !isMobileWidth(window.innerWidth)) {
      triggerPeek(5);
    }
  }, [setTheme]);

  useEffect(() => {
    const currentProject = projects.find((project) => {
      return project.slug === router.query.id;
    });

    const tl = gsap.timeline();
    if (currentProject) {
      tl.to(deliverablesTextRef.current, { alpha: 0 });
      tl.to(deliverablesTextRef.current, {
        onStart: () => {
          if (deliverablesTextRef.current) {
            deliverablesTextRef.current.innerHTML =
              currentProject.deliverables.replace(/\n/g, '<br />') || 'Case Study';
          }
        },
      });
      tl.to(deliverablesTextRef.current, { alpha: 1 });
      // setDeliverables(currentProject.deliverables.replace(/\n/g, '<br />'));
      if (currentProject.slug === project.slug) {
        setProjectToBeFetched(undefined);
      } else {
        setProjectToBeFetched(currentProject);
      }
    }
    return () => {
      tl.kill();
    };
  }, [router.query]);

  const triggerPeek = (delay: number = 0) => {
    gsap.to('#projectContent', {
      y: -30,
      // yoyo: true,
      // repeat: 1,
      // overwrite: true,
      // repeatDelay: 3,
      delay: delay,
      ease: 'circ.inOut',
    });
    gsap.to('#scrollDownButton', {
      y: -20,
      // yoyo: true,
      ease: 'circ.inOut',
      // overwrite: true,
      // repeat: 1,
      // rotate: 180,
      // scale: 0.9,
      // repeatDelay: 3,
      delay: delay + 0.1,
    });
  };
  const hidePeek = () => {
    gsap.to(['#projectContent', '#scrollDownButton'], { y: 0, overwrite: true });
  };

  return (
    <div className={style.container}>
      <CustomMetaData {...metaData} />
      <div className={style.carouselContainer}>
        <div className={style.backButtonContainer}>
          <BackToProjects />
        </div>
        <div
          ref={contentButton}
          className={style.contentButtonContainer}
          onMouseEnter={() => {
            triggerPeek();
          }}
          onMouseLeave={() => {
            hidePeek();
          }}
        >
          <div ref={deliverablesTextRef} />
          <button
            id="scrollDownButton"
            className={style.caseStudyButton}
            onClick={onCaseStudyClick}
          >
            <SVG className={style.svg} />
          </button>
        </div>
        <ProjectSelectorCarousel
          activeProjectSlug={project.slug}
          selectedProjectSlide={projectToBeFetched?.slug || project.slug}
          projects={projects}
          onProjectSelect={onCarouselSlideSelect}
        />

        <div ref={darkener} className={style.carouselDarkener} />
      </div>

      <div className={style.contentContainer}>
        <div className={style.content} id="projectContent">
          {/* we are loading dynamic data */}
          {projectToBeFetched && (
            <ProjectContent
              projectEndpoint={projectToBeFetched.url}
              blockClassNames={style.blocks}
              loaderClassName={style.loader}
            />
          )}
          {/* display the prefetched data */}
          {!projectToBeFetched && (
            <div className={style.standardBlocks}>
              <StandardBlocksLayout blocks={project.blocks} blockStyles={style.blocks} />
            </div>
          )}
          <div className={style.endOfContentContainer}>
            <div
              onMouseEnter={() => {
                gsap.to('#scrollButton', {
                  x: 20,
                  ease: 'circ.inOut',
                });
              }}
              onMouseLeave={() => {
                gsap.to('#scrollButton', {
                  x: -0,
                  ease: 'circ.inOut',
                });
              }}
            >
              <button
                id="scrollButton"
                className={style.backToTopButton}
                onClick={() => {
                  gsap.to(window, { duration: 0, scrollTo: 0 });
                  onNextClick();
                }}
              >
                <SVG className={style.backToTopSVG} />
              </button>
            </div>
            <button
              id="nextButton"
              className={style.nextProjectButton}
              onClick={() => {
                gsap.to(window, { duration: 0, scrollTo: 0 });
                onNextClick();
              }}
            >
              <SVG className={style.backToTopSVG} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Project;

// This function gets called at build time
export async function getStaticPaths(context) {
  // Call an external API endpoint to get posts
  const projects = await getEndpoint('/projects/', context);

  // Get the paths we want to pre-render based on posts
  const paths = projects.map((post, index) => {
    return {
      params: { id: post.slug },
    };
  });

  // We'll pre-render only these paths at build time.
  // { fallback: blocking } will server-render pages
  // on-demand if the path doesn't exist.
  return { paths, fallback: 'blocking' };
}

// This also gets called at build time
export async function getStaticProps(context) {
  const { params } = context;
  // params contains the project `id`.
  // If the route is like /project/1, then params.id is 1
  const projects = await getEndpoint('/projects/', context);
  // find project with correct slug
  const currentProject = projects.find((project) => {
    return project.slug === params.id;
  });

  // 404 page
  if (!currentProject) {
    // const slugs = projects.map((p) => p.slug);

    // Sentry.withScope((scope) => {
    //   slugs.forEach((s) => {
    //     scope.setExtra(s, s);
    //   });
    //   // Sentry.captureException(error);
    //   Sentry.captureMessage(`404 id:${params.id}.`, scope);
    // });
    return {
      notFound: true,
      revalidate: 10,
    };
  }

  const project = await getData(currentProject.url, context);

  project.blocks.forEach((block) => {
    if (block.block_type === 'media') {
      block.loadColor = '#EFEFEF';
    }
    if (block.block_type === 'gallery') {
      block.items.forEach((item) => {
        item.loadColor = '#EFEFEF';
      });
    }
  });

  // const project = projects.find((p) => p.slug === params.id);

  // Pass project data to the page via props
  const globalData = await getGlobalData();
  const props: Props = {
    project,
    projects: projects,
    globalData,
    metaData: {
      titleDefault: project.full_name,
      title: project.meta_title,
      description: project.meta_description,
      image: project.meta_image,
    },
  };
  return {
    props: props,
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 10 seconds
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS, // In seconds
  };
}
